import { useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useLocation, useParams, useReplacePath } from '../../../../contexts/RouterContext';
import { stripLastPathSegment } from '../../../../helpers/paths';
import { useOnMount } from '../../../../hooks';
import useRevisionMinimal from '../../../../hooks/api/revisions/useRevisionMinimal';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import COLORS from '../../../../theme/colors';
import { nuc } from '../../../../utils';
import { Icon, icons } from '../../../generic/Icon';
import { useProject } from '../../../projects/contexts/ProjectContext';
import RevisionDropdown from '../../../projects/DisplayProjectNav/RevisionDropdown/Dropdown';
import RevisionDisplay from '../../../projects/DisplayProjectNav/RevisionDropdown/RevisionDisplay';
import useRevision from '../../../projects/hooks/useRevision';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { Revision } from '../../../../types';

import StopComparing from './StopComparing';

import styles from '../IssueComparison.module.less';

const ComparisonHeader: VoidFunctionComponent = () => {
  // Get hooks ready
  const dispatch = useDispatch();
  const replacePath = useReplacePath();
  const location = useLocation();

  // Get context data
  const project = useProject();
  const revision = useRevision();

  const setComparisonRevisionAndFetch = useFetchIssuesAction(IssuesV2Store.actions.setComparisonRevision);

  // Get URL param data
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = useParams<{ reference: string; reference_type: string; compareRevisionId?: string }>();

  // Set up state
  const [comparisonRevision, setComparisonRevision] = useState<Revision | undefined>(undefined);

  const shouldFetchRevision = !!params.compareRevisionId && !comparisonRevision;
  const { data: urlRevision, isLoading } = useRevisionMinimal(
    `${project.data?.locator}$${params.compareRevisionId}` ?? '',
    shouldFetchRevision,
  );

  useEffect(() => {
    if (urlRevision) {
      setComparisonRevision(urlRevision as unknown as Revision);
    }
  }, [urlRevision]);

  // If we have a compareRevisionId in the URL params on mounting, then we load it into the Redux store.
  useOnMount(() => {
    if (params.compareRevisionId) {
      dispatch(IssuesV2Store.actions.setComparisonRevision(params.compareRevisionId));
    }
  });

  useEffect(() => {
    if (!comparisonRevision) {
      return;
    }

    const newBasePath = `${params.compareRevisionId ? stripLastPathSegment(location.pathname) : location.pathname}`;

    replacePath(`${newBasePath}/${nuc(comparisonRevision.loc.revision)}`, { search: location.search });
    setComparisonRevisionAndFetch(nuc(comparisonRevision.loc.revision));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- I only want this to run when the comparisonRevision changes.
  }, [comparisonRevision]);

  return (
    <header className={styles.headerCard}>
      {/* We use the container container-responsive class to ensure this lines up with the content below it. */}
      <div className="container container-responsive">
        <div className={styles.headerTitleSection}>
          <span className={styles.title}>Comparing Changes</span>
          <StopComparing />
        </div>
        Compare:{' '}
        <div className={styles.revisionComparisonContainer}>
          <div className={styles.revision}>
            <RevisionDisplay type={project.data?.type ?? 'unknown'} selectedRevision={revision.data} variant="light" />
          </div>
          <Icon icon={icons.arrowLeft} />
          {shouldFetchRevision && isLoading ? (
            <ContentLoader
              height="30px"
              width="30%"
              foregroundColor={COLORS.fossaGray05}
              backgroundColor={COLORS.fossaGray10}
            >
              <rect x="0" y="0" rx="5" ry="5" width="206px" height="30px" />
            </ContentLoader>
          ) : (
            <div className={styles.revision}>
              <RevisionDropdown
                selectedRevision={comparisonRevision}
                onSelectRevision={(r) => setComparisonRevision(r)}
                reference={params.reference}
                referenceType={params.reference_type}
                project={project}
                placeholder="Select a revision to compare"
                variant="light"
                startingRevision={revision.data?.locator}
                shouldFetchOnlyResolvedRevisions
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ComparisonHeader;
